import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  title: string;
  /** Šta se dešava ako se potvrdi. Piše se pre nego što čovek odluči, ne posle. */
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Radnja koja nešto oslobađa, briše ili gasi — dugme je tada crveno. */
  danger?: boolean;
  busy?: boolean;
  /** Prazno dok obavezno polje nije popunjeno. */
  disabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}

/**
 * Pitanje na koje čovek odgovara pre nego što se nešto desi.
 *
 * Zamenjuje `window.confirm` i `window.prompt`, kojih je bilo deset — i to na
 * najvažnijim odlukama: razlog izuzetka, odbacivanje ture, poništen nalog.
 * Razlozi su konkretni:
 *
 * - Dijalog pregledača <b>guta kontekst</b>: ne vidi se ono o čemu se odlučuje.
 * - Ne prima duži tekst i ne ume da ponudi izbor sa spiska.
 * - `prompt` vraća prazan tekst isto kao i odustajanje, pa se razlika pogađa.
 * - Na telefonu izgleda kao sistemska greška, a ne kao deo aplikacije.
 *
 * Escape zatvara, prvo polje dobija fokus, a klik pored NE zatvara — odluka koja
 * oslobađa robu ne sme da otpadne na promašen klik.
 */
export default function Dialog({
  title, description, confirmLabel, cancelLabel = 'Odustani',
  danger, busy, disabled, onConfirm, onCancel, children
}: Props) {
  const panel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };

    window.addEventListener('keydown', onKey);

    // Fokus ide na prvo polje ako ga ima, inače na sam dijalog — da tastatura
    // ne ostane iza, na strani koja je sada pokrivena.
    const first = panel.current?.querySelector<HTMLElement>('input, textarea, select, button');
    first?.focus();

    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return createPortal(
    <div className="dialog__veil">
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} ref={panel}>
        <h2 className="dialog__title">{title}</h2>

        {description && <div className="dialog__text">{description}</div>}

        {children && <div className="dialog__body">{children}</div>}

        <div className="actions actions--end">
          <button type="button" className="link" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? 'primary primary--danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy || disabled}
          >
            {busy ? 'Radim…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
